import { useCallback, useEffect } from 'react';
import { useSetImmerAtom, useImmerAtom } from 'jotai-immer';
import {
    defaultsTableStateAtom,
    defaultsTableViewAtom,
    defaultsTableStatsAtom,
    defaultsTableFiltersAtom,
    defaultsDataAtom,
} from './monitordefaults_atoms';
import { monitorDefaultsColumnSpec, monitorDefaultsDefaultView } from './MonitorDefaultsTableSpec';
import { tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { tableViewVM, tableStateVM, tableStatsVM } from '../../../../core/table/table_view_models';
import datamonitorRepo, { MonitorDefaultItem } from '../../data/datamonitorRepo';

type UseMonitorDefaultsTableInitReturn = {
    init: boolean;
    loadDefaults: () => void;
};

export const useMonitorDefaultsTableInit = (): UseMonitorDefaultsTableInitReturn => {
    const [{ init }, setState] = useImmerAtom(defaultsTableStateAtom);
    const setView = useSetImmerAtom(defaultsTableViewAtom);
    const setStats = useSetImmerAtom(defaultsTableStatsAtom);
    const setFilter = useSetImmerAtom(defaultsTableFiltersAtom);
    const setData = useSetImmerAtom(defaultsDataAtom);

    useEffect(() => {
        if (init) {
            const colSpec = monitorDefaultsColumnSpec();
            const defaultView = monitorDefaultsDefaultView();
            const view = tblSpecLoadView(defaultView.key, [defaultView], colSpec);
            setView(() => view || tableViewVM());
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setState(draft => {
                draft.views = [defaultView];
                draft.currentViewId = defaultView.key;
                draft.init = false;
            });
        }
    }, [init, setState, setView, setFilter, setStats]);

    const loadDefaults = useCallback(async (): Promise<void> => {
        // Reset filters and stats before fetching
        setFilter(() => ({}));
        setStats(() => tableStatsVM());
        setState(draft => {
            draft.waiting = true;
        });
        try {
            const items = await datamonitorRepo.getDefaults();
            setData(draft => {
                draft.dataItems = items as MonitorDefaultItem[];
                draft.items = [];
            });
        } catch {
            setData(() => ({ items: [], dataItems: [] }));
        } finally {
            setState(draft => {
                draft.waiting = false;
            });
        }
    }, [setState, setData, setFilter, setStats]);

    // Cleanup on unmount
    useEffect(() => {
        return (): void => {
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setData(() => ({ items: [], dataItems: [] }));
        };
    }, [setState, setView, setFilter, setStats, setData]);

    return { init, loadDefaults };
};
